<template>
  <div
    class="accruals-estimate-comments-tab"
    data-testid="accruals-estimate-comments-tab"
  >
    <div
      v-if="loading && !items.length"
      class="accruals-estimate-comments-tab__loading"
      data-testid="accruals-estimate-comments-tab__loading"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        size="32"
      />
    </div>

    <p
      v-else-if="!items.length"
      class="accruals-estimate-comments-tab__empty"
      data-testid="accruals-estimate-comments-tab__empty"
    >
      No comments yet. Actions and notes from preparers and approvers show up here.
    </p>

    <div
      v-else
      ref="list"
      class="accruals-estimate-comments-tab__list"
      data-testid="accruals-estimate-comments-tab__list"
    >
      <div
        v-for="(item, index) in items"
        :key="item.id"
      >
        <article
          v-if="item.kind === 'comment'"
          class="accruals-estimate-comments-tab__entry"
          :class="{ 'accruals-estimate-comments-tab__entry--pending': item.pending }"
          :data-testid="`accruals-estimate-comments-tab__entry--${index}`"
        >
          <div class="accruals-estimate-comments-tab__user">
            <span
              class="accruals-estimate-comments-tab__avatar"
              :class="avatarClass(item)"
            >{{ item.initials }}</span>
            <span class="accruals-estimate-comments-tab__name">{{ item.name }}</span>
            <span class="accruals-estimate-comments-tab__time">{{ item.time }}</span>
            <span
              v-if="item.tag"
              class="accruals-estimate-comments-tab__tag"
              :class="`accruals-estimate-comments-tab__tag--${tagTone(item.tag)}`"
            >{{ tagLabel(item.tag) }}</span>
          </div>
          <div class="accruals-estimate-comments-tab__bubble">
            <p class="accruals-estimate-comments-tab__body">{{ item.body }}</p>
            <div
              v-if="item.files && item.files.length"
              class="accruals-estimate-comments-tab__files"
            >
              <button
                v-for="file in item.files"
                :key="file.id"
                type="button"
                class="accruals-estimate-comments-tab__file"
                :data-testid="`accruals-estimate-comments-tab__file--${file.id}`"
                @click="downloadFile(file)"
              >
                {{ file.name }}
              </button>
            </div>
          </div>
        </article>
        <div
          v-else
          class="accruals-estimate-comments-tab__system"
          :data-testid="`accruals-estimate-comments-tab__system--${index}`"
        >
          <span>{{ item.text }}</span>
          <span class="accruals-estimate-comments-tab__system-time">{{ item.time }}</span>
        </div>
      </div>
    </div>

    <form
      v-if="canCompose"
      class="accruals-estimate-comments-tab__composer"
      data-testid="accruals-estimate-comments-tab__composer"
      @submit.prevent="onSend"
    >
      <span class="accruals-estimate-comments-tab__composer-avatar">{{ initials }}</span>
      <input
        :value="draft"
        type="text"
        class="accruals-estimate-comments-tab__input"
        placeholder="Add a note... type @ to mention"
        data-testid="accruals-estimate-comments-tab__input"
        @input="$emit('update:draft', $event.target.value)"
      />
      <base-button
        primary
        small
        type="submit"
        class="shadow-off"
        data-testid="accruals-estimate-comments-tab__send"
      >
        {{ sending ? 'Sending' : 'Send' }}
      </base-button>
    </form>
  </div>
</template>

<script>
import { ACCRUALS_COMMENT_TAG, ACCRUALS_COMMENT_TAG_CONFIG } from '../accrualsEstimatesConstants';

export default {
  name: 'AccrualsEstimateCommentsTab',
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    draft: {
      type: String,
      default: '',
    },
    sending: {
      type: Boolean,
      default: false,
    },
    initials: {
      type: String,
      default: '?',
    },
    canCompose: {
      type: Boolean,
      default: true,
    },
  },
  methods: {
    tagLabel(tag) {
      return ACCRUALS_COMMENT_TAG_CONFIG[tag]?.label || 'Remark';
    },
    tagTone(tag) {
      return ACCRUALS_COMMENT_TAG_CONFIG[tag]?.tone || 'neutral';
    },
    avatarClass(item) {
      if (item.tag === ACCRUALS_COMMENT_TAG.REJECTION) {
        return 'accruals-estimate-comments-tab__avatar--approver';
      }
      return 'accruals-estimate-comments-tab__avatar--preparer';
    },
    onSend() {
      if (this.sending) return;
      if (!String(this.draft || '').trim()) {
        this.$store.commit('sbError', 'Enter a comment before sending.', { root: true });
        return;
      }
      this.$emit('send');
    },
    downloadFile(file) {
      const id = file?.id;
      if (!id || !this.$nova?.downloadFile) return;
      this.$nova.downloadFile(id);
    },
    scrollToLatest() {
      const list = this.$refs.list;
      if (!list) return;
      list.scrollTop = list.scrollHeight;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './AccrualsEstimateCommentsTab.scss';
</style>
